import { useMemo, useState } from 'react';
import type { AccountIdUser } from '@/services/id-management.service';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { SearchInput } from '@/components/ui/search-input';

const STACKED_INITIALS = 4;
/** Up to this many users fit the popup without scrolling far, so search would only be clutter. */
const SEARCH_THRESHOLD = 8;

function initialsOf(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map((word) => word.charAt(0)).join('').toUpperCase();
}

function UserInitials({ name }: { name: string }) {
  return (
    // The solid outer circle stops overlapping tints showing through each other.
    <span className="shrink-0 rounded-full bg-card">
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
        {initialsOf(name)}
      </span>
    </span>
  );
}

interface Props {
  users: AccountIdUser[];
  /** Names what the list belongs to, for screen readers. */
  accountLabel: string;
}

/** One line for any number of users: overlapping initials and a count, with everyone in a popup. */
export function AccountUsersPopover({ users, accountLabel }: Props) {
  const [search, setSearch] = useState('');
  const countLabel = `${users.length} ${users.length === 1 ? 'user' : 'users'}`;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      u.name.toLowerCase().includes(q) || (u.employee_code ?? '').toLowerCase().includes(q));
  }, [users, search]);

  return (
    <Popover onOpenChange={(open) => { if (!open) setSearch(''); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Show ${countLabel} for ${accountLabel}`}
          className="inline-flex max-w-full items-center gap-2 rounded-full py-0.5 pr-2 text-left transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {/* A slight overlap: any more and each circle covers the next one's second initial. */}
          <span className="flex shrink-0 -space-x-1">
            {users.slice(0, STACKED_INITIALS).map((u) => (
              <span key={u.id} className="rounded-full ring-2 ring-card">
                <UserInitials name={u.name} />
              </span>
            ))}
          </span>
          <span className="whitespace-nowrap text-sm text-muted-foreground">{countLabel}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 max-w-[calc(100vw-2rem)] p-0">
        <div className="border-b border-border/60 px-3 py-2 text-sm font-semibold text-foreground">{countLabel}</div>
        {users.length > SEARCH_THRESHOLD && (
          <div className="border-b border-border/60 p-1.5">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search users…"
              containerClassName="w-full"
            />
          </div>
        )}
        <div className="thin-scrollbar max-h-64 overflow-y-auto overscroll-contain p-1">
          {filtered.length === 0 ? (
            <p className="p-4 text-center text-sm text-muted-foreground">No users found</p>
          ) : (
            filtered.map((u) => (
              <div key={u.id} className="flex items-center gap-2.5 rounded-md px-2 py-1.5">
                <UserInitials name={u.name} />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{u.name}</span>
                {u.employee_code && (
                  <span className="shrink-0 text-xs text-muted-foreground">{u.employee_code}</span>
                )}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
